import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Highlight {
  id: string;
  webinarId: string;
  startSeconds: number;
  endSeconds: number;
  title: string;
  description: string;
  engagementScore: number;
  clipUrl: string | null;
  status: string;
}

interface Chapter {
  title: string;
  startSeconds: number;
  endSeconds: number;
  summary: string;
}

interface ChatSummary {
  keyTopics: string[];
  commonQuestions: string[];
  sentiment: { positive: number; neutral: number; negative: number };
  participationRate: number;
}

type TabId = "highlights" | "chapters" | "chat";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0 && s > 0) return `${m}m ${s}s`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

// ---------------------------------------------------------------------------
// Engagement Score Bar
// ---------------------------------------------------------------------------

function EngagementBar({ score, maxScore }: { score: number; maxScore: number }) {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;

  const barColor =
    pct >= 75 ? "bg-[var(--color-success)]/50" :
    pct >= 50 ? "bg-[var(--color-warning)]/50" :
    pct >= 25 ? "bg-[var(--color-accent-action)]" :
    "bg-[var(--color-destructive)]/50";

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-full bg-muted">
        <div
          className={cn("h-2 rounded-full transition-all", barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-10 text-right text-xs text-muted-foreground">
        {score.toFixed(1)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sentiment Donut (CSS only)
// ---------------------------------------------------------------------------

function SentimentDonut({ sentiment }: { sentiment: ChatSummary["sentiment"] }) {
  const { positive, neutral, negative } = sentiment;
  const total = positive + neutral + negative;

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-sm text-muted-foreground">No sentiment data</p>
      </div>
    );
  }

  const posPct = Math.round((positive / total) * 100);
  const neuPct = Math.round((neutral / total) * 100);
  const negPct = 100 - posPct - neuPct;

  // CSS conic-gradient donut
  const posEnd = posPct;
  const neuEnd = posEnd + neuPct;

  const gradient = `conic-gradient(
    #22c55e 0% ${posEnd}%,
    #a1a1aa ${posEnd}% ${neuEnd}%,
    #ef4444 ${neuEnd}% 100%
  )`;

  return (
    <div className="flex items-center gap-6">
      <div className="relative h-32 w-32 shrink-0">
        <div
          className="h-32 w-32 rounded-full"
          style={{ background: gradient }}
        />
        <div className="absolute inset-3 rounded-full bg-background" />
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-[var(--color-success)]/50" />
          <span className="text-foreground">Positive</span>
          <span className="text-muted-foreground">{posPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-zinc-400" />
          <span className="text-foreground">Neutral</span>
          <span className="text-muted-foreground">{neuPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-[var(--color-destructive)]/50" />
          <span className="text-foreground">Negative</span>
          <span className="text-muted-foreground">{negPct}%</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Highlight Card
// ---------------------------------------------------------------------------

function HighlightCard({
  highlight,
  maxScore,
  onGenerateClip,
  isGenerating,
}: {
  highlight: Highlight;
  maxScore: number;
  onGenerateClip: (highlightId: string) => void;
  isGenerating: boolean;
}) {
  return (
    <div className="stagger-item rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">
              {formatTimestamp(highlight.startSeconds)}
            </span>
            <span className="text-xs text-muted-foreground">-</span>
            <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">
              {formatTimestamp(highlight.endSeconds)}
            </span>
            <span className="text-xs text-muted-foreground">
              ({formatDuration(highlight.endSeconds - highlight.startSeconds)})
            </span>
          </div>
          <h3 className="mt-2 text-sm font-semibold text-foreground">{highlight.title}</h3>
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{highlight.description}</p>
        </div>
      </div>

      <div className="mt-3">
        <p className="mb-1 text-xs text-muted-foreground">Engagement</p>
        <EngagementBar score={highlight.engagementScore} maxScore={maxScore} />
      </div>

      <div className="mt-3 flex items-center gap-2">
        {highlight.clipUrl ? (
          <a
            href={highlight.clipUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            Download Clip
          </a>
        ) : (
          <button
            onClick={() => onGenerateClip(highlight.id)}
            disabled={isGenerating || highlight.status === "processing"}
            className={cn(
              "rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors",
              isGenerating || highlight.status === "processing"
                ? "cursor-not-allowed text-muted-foreground"
                : "text-foreground hover:bg-accent",
            )}
          >
            {highlight.status === "processing" ? "Processing..." : "Generate Clip"}
          </button>
        )}
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs",
            highlight.status === "ready" ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
            highlight.status === "processing" ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" :
            "bg-muted text-muted-foreground",
          )}
        >
          {highlight.status}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chapters Timeline
// ---------------------------------------------------------------------------

function ChaptersTimeline({ chapters }: { chapters: Chapter[] }) {
  if (chapters.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No chapters available. Generate highlights first to create chapter markers.
        </p>
      </div>
    );
  }

  const lastChapter = chapters[chapters.length - 1];
  const totalDuration = lastChapter ? lastChapter.endSeconds : 0;

  return (
    <div className="space-y-0">
      {chapters.map((chapter, idx) => {
        const widthPct = totalDuration > 0
          ? ((chapter.endSeconds - chapter.startSeconds) / totalDuration) * 100
          : 100 / chapters.length;

        return (
          <div key={idx} className="group relative">
            {/* Timeline bar */}
            <div className="flex items-center gap-3">
              <div className="flex w-20 shrink-0 items-center justify-end">
                <span className="text-xs font-mono text-muted-foreground">
                  {formatTimestamp(chapter.startSeconds)}
                </span>
              </div>
              <div className="relative h-2 flex-1 rounded-full bg-muted">
                <div
                  className="absolute left-0 top-0 h-2 rounded-full bg-primary/60 transition-all group-hover:bg-primary"
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <div className="flex w-20 shrink-0 items-center">
                <span className="text-xs font-mono text-muted-foreground">
                  {formatTimestamp(chapter.endSeconds)}
                </span>
              </div>
            </div>

            {/* Chapter info */}
            <div className="ml-[calc(5rem+0.75rem)] mt-1 mb-4">
              <h4 className="text-sm font-medium text-foreground">{chapter.title}</h4>
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{chapter.summary}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat Summary View
// ---------------------------------------------------------------------------

function ChatSummaryView({ summary }: { summary: ChatSummary | null }) {
  if (!summary) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">Loading chat summary...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Participation */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 text-sm font-semibold text-foreground">Participation</h3>
        <p className="text-3xl font-bold text-foreground">
          {Math.round(summary.participationRate * 100)}%
        </p>
        <p className="text-xs text-muted-foreground">of attendees participated in chat</p>
      </div>

      {/* Sentiment */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-4 text-sm font-semibold text-foreground">Sentiment Analysis</h3>
        <SentimentDonut sentiment={summary.sentiment} />
      </div>

      {/* Key Topics */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">Key Topics</h3>
        {summary.keyTopics.length === 0 ? (
          <p className="text-xs text-muted-foreground">No topics detected.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {summary.keyTopics.map((topic) => (
              <span
                key={topic}
                className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-foreground"
              >
                {topic}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Common Questions */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">
          Common Questions ({summary.commonQuestions.length})
        </h3>
        {summary.commonQuestions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No questions detected in chat.</p>
        ) : (
          <ul className="space-y-2">
            {summary.commonQuestions.map((q, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-0.5 shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                  Q
                </span>
                <span className="text-foreground">{q}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function WebinarHighlightsPage() {
  const { webinarId } = useParams();
  const webinarBasePath = webinarId ? `/v1/webinars/${webinarId}` : null;
  const [activeTab, setActiveTab] = useState<TabId>("highlights");
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [chatSummary, setChatSummary] = useState<ChatSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingClipId, setGeneratingClipId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchHighlights = useCallback(async () => {
    if (!webinarBasePath) return;
    try {
      const res = await apiFetch<{ data: Highlight[] }>(
        `${webinarBasePath}/highlights`,
      );
      setHighlights(res.data ?? []);
    } catch {
      setHighlights([]);
    }
  }, [webinarBasePath]);

  const fetchChapters = useCallback(async () => {
    if (!webinarBasePath) return;
    try {
      const res = await apiFetch<{ chapters: Chapter[] }>(
        `${webinarBasePath}/highlights/chapters`,
      );
      setChapters(res.chapters ?? []);
    } catch {
      setChapters([]);
    }
  }, [webinarBasePath]);

  const fetchChatSummary = useCallback(async () => {
    if (!webinarBasePath) return;
    try {
      const res = await apiFetch<ChatSummary>(
        `${webinarBasePath}/highlights/chat-summary`,
      );
      setChatSummary(res);
    } catch {
      setChatSummary(null);
    }
  }, [webinarBasePath]);

  useEffect(() => {
    if (!webinarId) return;
    setIsLoading(true);

    Promise.all([fetchHighlights(), fetchChapters(), fetchChatSummary()])
      .catch(() => setError("Failed to load highlights data"))
      .finally(() => setIsLoading(false));
  }, [webinarId, fetchHighlights, fetchChapters, fetchChatSummary]);

  const handleGenerateAll = useCallback(async () => {
    if (!webinarBasePath || isGenerating) return;
    setIsGenerating(true);
    setError(null);

    try {
      await apiFetch(`${webinarBasePath}/highlights/generate`, {
        method: "POST",
        body: JSON.stringify({
          transcriptUrl: `${webinarBasePath}/captions/transcript`,
          engagementData: { timestamps: [], chatDensity: [], pollActivity: [] },
        }),
      });

      // Refresh data after a brief delay to allow processing
      await fetchHighlights();
      await fetchChapters();
    } catch {
      setError("Failed to generate highlights. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [webinarBasePath, isGenerating, fetchHighlights, fetchChapters]);

  const handleGenerateClip = useCallback(async (highlightId: string) => {
    if (!webinarBasePath) return;
    setGeneratingClipId(highlightId);

    const highlight = highlights.find((h) => h.id === highlightId);
    if (!highlight) return;

    try {
      await apiFetch(`${webinarBasePath}/highlights/${highlightId}/clip`, {
        method: "POST",
        body: JSON.stringify({
          format: "mp4",
          startSeconds: highlight.startSeconds,
          endSeconds: highlight.endSeconds,
          includeCaption: true,
        }),
      });

      // Refresh highlights to show updated status
      await fetchHighlights();
    } catch {
      setError("Failed to generate clip. Please try again.");
    } finally {
      setGeneratingClipId(null);
    }
  }, [webinarBasePath, highlights, fetchHighlights]);

  if (!webinarId) return null;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-96" />
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-destructive">{error}</p>
          <button
            onClick={() => { setError(null); window.location.reload(); }}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const maxScore = highlights.length > 0
    ? Math.max(...highlights.map((h) => h.engagementScore))
    : 1;

  const tabs: Array<{ id: TabId; label: string; count?: number }> = [
    { id: "highlights", label: "Highlights", count: highlights.length },
    { id: "chapters", label: "Chapters", count: chapters.length },
    { id: "chat", label: "Chat Summary" },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/webinars/${webinarId}/analytics`}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Back to Analytics
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-foreground">
            AI Highlights & Clips
          </h1>
        </div>
        <button
          onClick={handleGenerateAll}
          disabled={isGenerating}
          className={cn(
            "rounded-md px-4 py-2 text-sm font-medium transition-colors",
            isGenerating
              ? "cursor-not-allowed bg-muted text-muted-foreground"
              : "bg-primary text-primary-foreground hover:bg-primary/90",
          )}
        >
          {isGenerating ? "Generating..." : "Generate All Highlights"}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 rounded-lg border border-border p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "rounded-md px-4 py-2 text-sm font-medium transition-colors",
              activeTab === tab.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent",
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 rounded-full bg-background/20 px-1.5 py-0.5 text-xs">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "highlights" && (
        <div>
          {highlights.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No highlights detected yet. Click "Generate All Highlights" to analyze this webinar.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {highlights.map((h) => (
                <HighlightCard
                  key={h.id}
                  highlight={h}
                  maxScore={maxScore}
                  onGenerateClip={handleGenerateClip}
                  isGenerating={generatingClipId === h.id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "chapters" && (
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold text-foreground">Chapter Timeline</h2>
          <ChaptersTimeline chapters={chapters} />
        </div>
      )}

      {activeTab === "chat" && <ChatSummaryView summary={chatSummary} />}
    </div>
  );
}
